import { WidgetID } from '@/widget-store';
import type {
  DefaultValues,
  Step,
} from '@/features/agents/components/capabilities-studio-widget';
import { Navigation } from '@/features/agents/components/navigation';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/button';
import { Editor } from '../editor';
import { useCreateCapabilityProfile } from '@/features/agents/hooks/use-create-capability-profile';
import { useParams } from 'react-router-dom';
import { PromptBuilder } from '@/components/prompt-builder';
import { DragDrop } from '@/components/drag-drop';
import { ArrowPathIcon, LinkIcon, LockClosedIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { XCircleIcon } from '@heroicons/react/20/solid';
import { ObjectParams } from '@/hooks/use-relation-insert';
import * as Popover from '@radix-ui/react-popover';
import { ObjectPickerForm } from '@/components/object-picker-form';
import { useGenerateInputSchemaFromFunction } from '@/features/agents/hooks/use-generate-input-schema-from-function';
import { useGenerateConfigurationSchemaFromFunction } from '@/features/agents/hooks/use-generate-configuration-schema-from-function';
import { useGenerateInputSchemaFromExample } from '@/features/agents/hooks/use-generate-input-schema-from-example';
import { useGenerateOutputSchemaFromFunction } from '@/features/agents/hooks/use-generate-output-schema-from-function';
import { useGenerateOutputSchemaFromExample } from '@/features/agents/hooks/use-generate-output-schema-from-example';
import { useEffect, useState } from 'react';
import { useGenerateTags } from '@/features/agents/hooks/use-generate-tags';
import { useGenerateDescription } from '@/features/agents/hooks/use-generate-description';
import { useGenerateDocumentation } from '@/features/agents/hooks/use-generate-documentation';
import { useQueryClient, useIsFetching } from '@tanstack/react-query';
import { useUserDataclasses } from '@/hooks/user/use-user-dataclasses';

export interface DescribeFormInputs {
  title: string;
  tags: string;
  description: string;
  documentation: string;
  offerInCapabilityStore: boolean;
  exemplaryInput: string;
  exemplaryOutput: string;
  exemplaryConfiguration: string;
  inputSchema: string;
  outputSchema: string;
  vaultSchemas: {
    uuid: string;
    container: string;
    label?: string;
    variableName?: string;
  }[];
}

interface AgentData {
  inputSchema?: any;
  outputSchema?: any;
  documentation?: string;
  exemplaryInput?: string;
  exemplaryOutput?: string;
  configurationVariables?: string[];
}

interface DescribeFormProps {
  id: WidgetID;
  defaultValues: DefaultValues['describe'];
  onChange: (data: DescribeFormInputs, step: 'describe') => void;
  steps: Step[];
  agent: AgentData | null;
  isLoading: boolean;
}

function formatVarName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DescribeForm({
  defaultValues,
  onChange,
  steps,
  agent: agentData,
  isLoading: isAgentLoading,
}: DescribeFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<DescribeFormInputs>({
    defaultValues,
  });
  const queryClient = useQueryClient();
  const { data: dataclassesData } = useUserDataclasses('vault');
  const dataclassOptions = (dataclassesData?.dataClasses || []).map((dc) => ({
    value: dc.value,
    label: dc.label,
  }));
  const isLoading = false;
  const createCapabilityProfileMutation = useCreateCapabilityProfile();
  const { uuid } = useParams();
  const isAgentRefetching = useIsFetching({ queryKey: ['agent', uuid] }) > 0;
  const isDataclassesRefetching = useIsFetching({ queryKey: ['userDataclasses', 'vault'] }) > 0;
  const generateInputSchemaFromFunctionMutation =
    useGenerateInputSchemaFromFunction();
  const generateInputSchemaFromExampleMutation =
    useGenerateInputSchemaFromExample();
  const generateOutputSchemaFromFunctionMutation =
    useGenerateOutputSchemaFromFunction();
  const generateOutputSchemaFromExampleMutation =
    useGenerateOutputSchemaFromExample();
  const generateTagsMutation = useGenerateTags();
  const generateDescriptionMutation = useGenerateDescription();
  const generateDocumentationMutation = useGenerateDocumentation();
  const generateConfigurationSchemaMutation = useGenerateConfigurationSchemaFromFunction();

  function handleCreateNewVaultSchema(varName: string) {
    generateConfigurationSchemaMutation.mutate(
      { agentUuid: uuid!, variableName: varName },
      {
        onSuccess: (data) => {
          const params = new URLSearchParams({
            schema: data.schema || '{}',
            title: formatVarName(varName),
            schemaType: 'vaultItem',
          });
          window.open(`/schemas?${params.toString()}`, '_blank');
        },
        onError: () => {
          const params = new URLSearchParams({
            title: formatVarName(varName),
            schemaType: 'vaultItem',
          });
          window.open(`/schemas?${params.toString()}`, '_blank');
        },
      }
    );
  }
  const [generatedDocumentation, setGeneratedDocumentation] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [openSchemaPopover, setOpenSchemaPopover] = useState<string | null>(null);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    if (!agentData) return;
    if (agentData.inputSchema && !defaultValues?.inputSchema) {
      const schema = Array.isArray(agentData.inputSchema)
        ? JSON.stringify(agentData.inputSchema)
        : agentData.inputSchema;
      setValue('inputSchema', schema);
    }
    if (agentData.outputSchema && !defaultValues?.outputSchema) {
      const schema = Array.isArray(agentData.outputSchema)
        ? JSON.stringify(agentData.outputSchema)
        : agentData.outputSchema;
      setValue('outputSchema', schema);
    }
  }, [agentData]);

  function onSubmit(data: DescribeFormInputs) {
    const activeVars = new Set(
      (agentData?.configurationVariables || []).filter((v) => !v.includes('.'))
    );
    const mutationData = {
      ...data,
      vaultSchemas: (data.vaultSchemas || []).filter(
        (s) => s.variableName && activeVars.has(s.variableName)
      ),
      uuid: uuid!,
    };

    createCapabilityProfileMutation.mutate(mutationData, {
      onSuccess: () => {
        setSaveError(null);
        queryClient.invalidateQueries({ queryKey: ['capabilities'] });
        queryClient.invalidateQueries({ queryKey: ['agent', uuid] });
        onChange(data, 'describe');
      },
      onError: (error: unknown) => {
        setSaveError(
          error instanceof Error ? error.message : 'Failed to save. Please try again.'
        );
      },
    });
  }

  const vaultSchemas = watch('vaultSchemas');

  function handleDrop(
    data: Record<string, unknown>,
    field: keyof DescribeFormInputs,
    variableName: string
  ) {
    const value = data.value as (ObjectParams & { label?: string })[];
    const newItem = { uuid: value[0].uuid, container: value[0].container, label: value[0].label, variableName };
    const newItems = [
      ...(vaultSchemas || []).filter((i) => i.variableName !== variableName),
      newItem,
    ];
    setValue(field, newItems);
    onChange({ ...defaultValues, vaultSchemas: newItems } as DescribeFormInputs, 'describe');
  }

  function handleGenerateInputSchemaFromFunction() {
    generateInputSchemaFromFunctionMutation.mutate(uuid!, {
      onSuccess: (data) => {
        setValue('inputSchema', data.schema);
        onChange(
          {
            ...defaultValues,
            inputSchema: data.schema,
          } as DescribeFormInputs,
          'describe'
        );
      },
    });
  }

  const exemplaryInput = watch('exemplaryInput');

  function handleGenerateInputSchemaFromExample() {
    generateInputSchemaFromExampleMutation.mutate(
      {
        agentUuid: uuid!,
        exampleSchema: exemplaryInput || '',
      },
      {
        onSuccess: (data) => {
          setValue('inputSchema', data.schema);
          onChange(
            {
              ...defaultValues,
              inputSchema: data.schema,
            } as DescribeFormInputs,
            'describe'
          );
        },
      }
    );
  }

  function handleGenerateOutputSchemaFromFunction() {
    generateOutputSchemaFromFunctionMutation.mutate(uuid!, {
      onSuccess: (data) => {
        setValue('outputSchema', data.schema);
        onChange(
          {
            ...defaultValues,
            outputSchema: data.schema,
          } as DescribeFormInputs,
          'describe'
        );
      },
    });
  }

  const exemplaryOutput = watch('exemplaryOutput');

  function handleGenerateOutputSchemaFromExample() {
    generateOutputSchemaFromExampleMutation.mutate(
      {
        agentUuid: uuid!,
        exampleSchema: exemplaryOutput || '',
      },
      {
        onSuccess: (data) => {
          setValue('outputSchema', data.schema);
          onChange(
            {
              ...defaultValues,
              outputSchema: data.schema,
            } as DescribeFormInputs,
            'describe'
          );
        },
      }
    );
  }

  const inputSchema = Array.isArray(agentData?.inputSchema)
    ? JSON.stringify(agentData?.inputSchema)
    : agentData?.inputSchema;

  const outputSchema = Array.isArray(agentData?.outputSchema)
    ? JSON.stringify(agentData?.outputSchema)
    : agentData?.outputSchema;

  function handleGenerateTags() {
    generateTagsMutation.mutate(
      {
        agentUuid: uuid!,
      },
      {
        onSuccess: (data) => {
          setValue('tags', data);
          onChange(
            {
              ...defaultValues,
              tags: data,
            } as DescribeFormInputs,
            'describe'
          );
        },
      }
    );
  }

  function handleGenerateDescription() {
    generateDescriptionMutation.mutate(
      {
        agentUuid: uuid!,
      },
      {
        onSuccess: (data) => {
          setValue('description', data);
          onChange(
            {
              ...defaultValues,
              description: data,
            } as DescribeFormInputs,
            'describe'
          );
        },
      }
    );
  }

  function handleGenerateDocumentation() {
    generateDocumentationMutation.mutate(
      {
        agentUuid: uuid!,
      },
      {
        onSuccess: (data) => {
          setValue('documentation', data);
          setGeneratedDocumentation(data);
        },
      }
    );
  }

  return (
    <>
      <Navigation
        loadingStep={isLoading ? 'describe' : undefined}
        steps={steps}
      />
      {isAgentLoading ? (
        <div>Loading...</div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-7 grid gap-y-7 gap-x-10 grid-cols-2 items-start"
        >
          <div className="grid gap-7">
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Title
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  id="title"
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                  {...register('title', {
                    required: true,
                    onChange: (e) => {
                      onChange(
                        {
                          ...defaultValues,
                          title: e.target.value,
                        } as DescribeFormInputs,
                        'describe'
                      );
                    },
                  })}
                />
                {errors.title && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                    Title is required.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-x-4">
              <div className="flex-1">
                <label
                  htmlFor="tags"
                  className="block text-sm font-medium text-gray-900 dark:text-gray-100"
                >
                  Tags
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="tags"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                    {...register('tags', {
                      onChange: (e) => {
                        onChange(
                          {
                            ...defaultValues,
                            tags: e.target.value,
                          } as DescribeFormInputs,
                          'describe'
                        );
                      },
                    })}
                  />
                </div>
              </div>
              <div className="flex-none mt-6">
                <Button
                  variant="primary"
                  title="Generate Tags"
                  onClick={handleGenerateTags}
                  loading={generateTagsMutation.isLoading}
                >
                  Generate
                </Button>
              </div>
            </div>
            <div>
              <div className="flex gap-x-4">
                <div className="flex-1">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-900 dark:text-gray-100"
                  >
                    Description
                  </label>
                  <div className="mt-1">
                    <textarea
                      id="description"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                      {...register('description', {
                        onChange: (e) => {
                          onChange(
                            {
                              ...defaultValues,
                              description: e.target.value,
                            } as DescribeFormInputs,
                            'describe'
                          );
                        },
                      })}
                    ></textarea>
                  </div>
                </div>
                <div className="flex-none mt-6">
                  <Button
                    variant="primary"
                    title="Generate Description"
                    onClick={handleGenerateDescription}
                    loading={generateDescriptionMutation.isLoading}
                  >
                    Generate
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-7">
            <div>
              <div className="flex gap-x-4">
                <div className="flex-1">
                  <label
                    htmlFor="documentation"
                    className="block text-sm font-medium text-gray-900 dark:text-gray-100"
                  >
                    Documentation
                  </label>
                  <div className="mt-1">
                    <PromptBuilder
                      content={
                        generatedDocumentation ||
                        defaultValues?.documentation ||
                        agentData?.documentation ||
                        ''
                      }
                      onChange={(value) => {
                        setValue('documentation', value);
                        onChange(
                          {
                            ...defaultValues,
                            documentation: value,
                          } as DescribeFormInputs,
                          'describe'
                        );
                      }}
                      outputFormat="html"
                      showMenuBar
                    />
                    <input
                      id="documentation"
                      type="hidden"
                      {...register('documentation')}
                    />
                  </div>
                </div>
                <div className="flex-none mt-6">
                  <Button
                    variant="primary"
                    title="Generate Documentation"
                    onClick={handleGenerateDocumentation}
                    loading={generateDocumentationMutation.isLoading}
                  >
                    Generate
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-7 col-span-2">
            <div>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Exemplary Input
              </span>
              <Editor
                className="h-40 block w-full rounded-md border-0 p-1 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                defaultLanguage="json"
                value={
                  defaultValues?.exemplaryInput ||
                  (agentData?.exemplaryInput
                    ? JSON.stringify(
                        JSON.parse(agentData?.exemplaryInput),
                        undefined,
                        4
                      )
                    : '')
                }
                onChange={(value) => {
                  setValue('exemplaryInput', value || '');
                  onChange(
                    {
                      ...defaultValues,
                      exemplaryInput: value || '',
                    } as DescribeFormInputs,
                    'describe'
                  );
                }}
                showLanguageSelector={false}
              />
              <input
                id="exemplaryInput"
                type="hidden"
                {...register('exemplaryInput')}
              />
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Exemplary Output
              </span>
              <Editor
                className="h-40 block w-full rounded-md border-0 p-1 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                defaultLanguage="json"
                value={
                  defaultValues?.exemplaryOutput ||
                  (exemplaryOutput
                    ? JSON.stringify(JSON.parse(exemplaryOutput), undefined, 4)
                    : '')
                }
                onChange={(value) => {
                  setValue('exemplaryOutput', value || '');
                  onChange(
                    {
                      ...defaultValues,
                      exemplaryOutput: value || '',
                    } as DescribeFormInputs,
                    'describe'
                  );
                }}
                showLanguageSelector={false}
              />
              <input
                id="exemplaryOutput"
                type="hidden"
                {...register('exemplaryOutput')}
              />
            </div>
            <div className="row-span-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                  Vault Schemas
                </span>
                <button
                  type="button"
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['agent', uuid] })}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  title="Refresh vault schemas"
                >
                  <ArrowPathIcon className={`h-4 w-4${isAgentRefetching ? ' animate-spin' : ''}`} />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                {(agentData?.configurationVariables || []).filter((v) => !v.includes('.')).length === 0 && (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 dark:border-gray-700 py-6 px-4 text-center">
                    <LockClosedIcon className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      No vault configuration found in agent
                    </p>
                  </div>
                )}
                {(agentData?.configurationVariables || [])
                  .filter((v) => !v.includes('.'))
                  .map((varName) => {
                    const item = (vaultSchemas || []).find(
                      (i) => i.variableName === varName
                    );
                    return (
                      <div key={varName}>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{formatVarName(varName)}</p>
                        {item ? (
                          <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-2 pr-3 shadow-sm text-xs">
                            <div className="relative w-10 h-10 bg-purple-50 dark:bg-purple-900/50 rounded-full flex justify-center items-center text-center">
                              <LinkIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {item.label || item.container}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const newItems = (vaultSchemas || []).filter(
                                  (i) => i.variableName !== varName
                                );
                                setValue('vaultSchemas', newItems);
                                onChange(
                                  { ...defaultValues, vaultSchemas: newItems } as DescribeFormInputs,
                                  'describe'
                                );
                              }}
                              className="text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <Popover.Root
                            open={openSchemaPopover === varName}
                            onOpenChange={(open) => setOpenSchemaPopover(open ? varName : null)}
                          >
                            <Popover.Anchor asChild>
                              <div onClick={() => setOpenSchemaPopover(varName)}>
                                <DragDrop
                                  allowedTypes={['object/insert']}
                                  onDrop={(data: Record<string, unknown>) =>
                                    handleDrop(data, 'vaultSchemas', varName)
                                  }
                                  className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-500 border-dashed justify-center text-center h-5 bg-white dark:bg-gray-800 cursor-pointer"
                                  droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-500 border-dashed bg-purple-50 dark:bg-purple-900/50 flex justify-center items-center h-5"
                                >
                                  {({ isDropping }) =>
                                    isDropping ? (
                                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                                    ) : (
                                      <PlusIcon className="h-4 w-4 text-gray-900 dark:text-gray-100" />
                                    )
                                  }
                                </DragDrop>
                              </div>
                            </Popover.Anchor>
                            <Popover.Portal>
                              <Popover.Content
                                className="relative bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                                sideOffset={5}
                              >
                                <ObjectPickerForm
                                  type="dataclass"
                                  label="Vault Schema"
                                  options={dataclassOptions}
                                  onCreateNew={() => handleCreateNewVaultSchema(varName)}
                                  createNewLoading={generateConfigurationSchemaMutation.isLoading}
                                  onSubmit={(option) => {
                                    if (!option) return;
                                    handleDrop(
                                      { value: [{ uuid: option.value, container: 'dataclass', label: option.label }] },
                                      'vaultSchemas',
                                      varName
                                    );
                                    setOpenSchemaPopover(null);
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => queryClient.invalidateQueries({ queryKey: ['userDataclasses', 'vault'] })}
                                  className="absolute top-2 right-8 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                  title="Refresh vault schemas"
                                >
                                  <ArrowPathIcon className={`h-4 w-4${isDataclassesRefetching ? ' animate-spin' : ''}`} />
                                </button>
                                <Popover.Close className="absolute top-2 right-2" aria-label="Close">
                                  <XMarkIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                </Popover.Close>
                                <Popover.Arrow asChild style={{ filter: 'drop-shadow(0 -3px 6px rgba(0,0,0,0.08))' }}>
                                  <svg width="10" height="5" viewBox="0 0 10 5">
                                    <polygon points="0,0 5,5 10,0" className="fill-white dark:fill-gray-800" />
                                    <polyline points="0,0 5,5 10,0" className="stroke-gray-200 dark:stroke-gray-700" strokeWidth="1" fill="none" />
                                  </svg>
                                </Popover.Arrow>
                              </Popover.Content>
                            </Popover.Portal>
                          </Popover.Root>
                        )}
                      </div>
                    );
                  })}
                <input type="hidden" {...register('vaultSchemas')} />
              </div>
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Input Schema
              </span>
              <Editor
                className="h-40 block w-full rounded-md border-0 p-1 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                defaultLanguage="json"
                value={
                  defaultValues?.inputSchema ||
                  (inputSchema
                    ? JSON.stringify(JSON.parse(inputSchema), undefined, 4)
                    : '')
                }
                showLanguageSelector={false}
                onChange={(value) => {
                  setValue('inputSchema', value || '');
                  onChange(
                    {
                      ...defaultValues,
                      inputSchema: value || '',
                    } as DescribeFormInputs,
                    'describe'
                  );
                }}
              />
              <input
                id="inputSchema"
                type="hidden"
                {...register('inputSchema')}
              />
              <div className="flex gap-4 mt-2">
                <Button
                  variant="primary"
                  onClick={handleGenerateInputSchemaFromFunction}
                  loading={generateInputSchemaFromFunctionMutation.isLoading}
                >
                  Generate
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGenerateInputSchemaFromExample}
                  loading={generateInputSchemaFromExampleMutation.isLoading}
                >
                  Generate from Example
                </Button>
              </div>
            </div>
            <div>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Output Schema
              </span>
              <Editor
                className="h-40 block w-full rounded-md border-0 p-1 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                defaultLanguage="json"
                value={
                  defaultValues?.outputSchema ||
                  (outputSchema
                    ? JSON.stringify(JSON.parse(outputSchema), undefined, 4)
                    : '')
                }
                showLanguageSelector={false}
                onChange={(value) => {
                  setValue('outputSchema', value || '');
                  onChange(
                    {
                      ...defaultValues,
                      outputSchema: value || '',
                    } as DescribeFormInputs,
                    'describe'
                  );
                }}
              />
              <input
                id="outputSchema"
                type="hidden"
                {...register('outputSchema')}
              />
              <div className="flex gap-4 mt-2">
                <Button
                  variant="primary"
                  onClick={handleGenerateOutputSchemaFromFunction}
                  loading={generateOutputSchemaFromFunctionMutation.isLoading}
                >
                  Generate
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGenerateOutputSchemaFromExample}
                  loading={generateOutputSchemaFromExampleMutation.isLoading}
                >
                  Generate from Example
                </Button>
              </div>
            </div>
          </div>
          {/* <div className="col-span-2">
            <div className="flex items-center gap-x-2">
              <input
                type="checkbox"
                id="offerInCapabilityStore"
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-500 focus:ring-indigo-600 dark:focus:ring-indigo-500 bg-white dark:bg-gray-800"
                {...register('offerInCapabilityStore')}
              />
              <label
                htmlFor="offerInCapabilityStore"
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Offer in capability store
              </label>
            </div>
          </div> */}
          <div className="col-span-2">
            {saveError && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 mb-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                      Save failed
                    </h3>
                    <div className="mt-1 text-sm text-red-700 dark:text-red-400">
                      <p>{saveError}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Button
              variant="tertiary"
              title="Save Capability Profile"
              type="submit"
              loading={createCapabilityProfileMutation.isLoading}
            >
              Save
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
